/**
 * useOsmdRenderer
 *
 * Lightweight OpenSheetMusicDisplay renderer for small MusicXML fragments
 * (e.g. a single Griffschrift measure variant). Each consumer owns its own OSMD
 * instance. Rendering is deferred until the container becomes visible
 * (IntersectionObserver) to keep many simultaneous tiles cheap, and the instance
 * is disposed on unmount to avoid leaking SVG/DOM nodes.
 */
import {type MaybeRefOrGetter, onBeforeUnmount, ref, toValue, watch} from 'vue';
import {useIntersectionObserver} from '@vueuse/core';
import {OpenSheetMusicDisplay} from 'opensheetmusicdisplay';
import type {IOSMDOptions} from "osmd-types-player";
import {disableOsmdInternalInteraction} from '@/composables/useOsmdInteractiveScore';

export interface UseOsmdRendererOptions {
    zoom?: number;
}

export function useOsmdRenderer(
    musicXml: MaybeRefOrGetter<string | null | undefined>,
    options: UseOsmdRendererOptions = {},
    iosmdOptions?: MaybeRefOrGetter<IOSMDOptions | null | undefined>,
) {
    const {zoom = 0.5} = options;

    const containerRef = ref<HTMLDivElement | null>(null);
    const isRendering = ref(false);
    const error = ref<string | null>(null);

    let osmd: OpenSheetMusicDisplay | null = null;
    let hasBeenVisible = false;

    async function render(): Promise<void> {
        const xml = toValue(musicXml);
        const el = containerRef.value;
        if (!el || !xml) return;

        isRendering.value = true;
        error.value = null;
        try {
            if (!osmd) {
                osmd = new OpenSheetMusicDisplay(el, {
                    // autoResize AUS (kritisch – nicht auf true rückbauen): OSMDs Resize-Handler
                    // ruft `renderAndScrollBack()`, und das ist ein echtes
                    // `window.scrollTo({top: document.body.scrollHeight * ratio})` – bei jedem
                    // Fenster-Resize sprang die Seite. Die Kacheln sind statische Thumbnails;
                    // Render-Auslöser ist der `watch(musicXml)` weiter unten.
                    autoResize: false,
                    backend: 'svg',
                    // performanceMode + hoher Batch-Schwellwert: verhindert OSMDs SkyBottomLine-
                    // Batch-Canvas + getImageData-OOM bei Vollpartitur-Vorschauen (z. B.
                    // FileHistoryPreview) und erzwingt den reinen SkyBottomLineCalculatorSVG.
                    performanceMode: true,
                    skyBottomLineBatchMinMeasures: Number.MAX_SAFE_INTEGER,
                    drawTitle: false,
                    drawSubtitle: false,
                    drawComposer: false,
                    drawLyricist: false,
                    drawTimeSignatures: false,

                    drawCredits: false,
                    drawPartNames: false,
                    drawMeasureNumbers: false,
                    ...(toValue(iosmdOptions) ?? {})
                });
                osmd.zoom = zoom;
                // OSMDs interner Interaktions-Manager registriert bei jedem load() Scroll-Listener
                // an allen scrollbaren Vorfahren (z. B. dem overflow-auto der FileHistoryPreview),
                // ohne die alten abzumelden – verwaiste Listener crashen nach dispose() beim
                // nächsten Scrollen ("reading '0'"). Abhängen direkt nach der Konstruktion.
                disableOsmdInternalInteraction(osmd);
            }
            await osmd.load(xml);
            await osmd.render();
        } catch (e) {
            error.value = e instanceof Error ? e.message : String(e);
            if (containerRef.value) containerRef.value.innerHTML = '';
        } finally {
            isRendering.value = false;
        }
    }

    // Defer the first render until the tile is actually visible.
    const {stop} = useIntersectionObserver(containerRef, (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !hasBeenVisible) {
            hasBeenVisible = true;
            void render();
        }
    });

    // Re-render when the fragment changes (only once it has been shown).
    watch(
        () => toValue(musicXml),
        () => {
            if (hasBeenVisible) void render();
        },
    );

    function dispose(): void {
        stop();
        try {
            osmd?.clear();
            // clear() UND dispose() (OSMD-Playbook): erst dispose() gibt intern gehaltene
            // Ressourcen/Listener frei; guarded, weil eine nie gerenderte Instanz nicht werfen darf.
            osmd?.dispose();
        } catch {
            /* ignore */
        }
        osmd = null;
    }

    onBeforeUnmount(dispose);

    return {containerRef, isRendering, error};
}
